<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-console, vue/no-unused-properties, no-use-before-define */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
  storageMigrationStatus: { type: String, required: false, default: '' },
  folderStyleStatus: { type: String, required: false, default: '' },
  existenceStatus: { type: String, required: false, default: '' },
});

const { t } = useI18n();

const loading = ref(false);
const loadingDetailed = ref(false);
const error = ref(null);
const hasRun = ref(false);
const progress = ref({ status: 'not_started', processed: 0, total: 0 });
const showLastExecution = ref(false); // hidden by default, will be shown when user toggles
const result = ref({ migrated: 0, total: 0 });
const currentSessionId = ref(null);
let pollInterval = null;
let elapsedInterval = null;
let rerunStateInterval = null;
let rerunStateTimeout = null;
let startTime = null;
let analysisDate = ref(null);
const elapsedToSeconds = value => {
  const text = String(value || '').trim();
  if (!text) return 0;
  const minuteMatch = text.match(/^(\d+)m\s*(\d+)s$/);
  if (minuteMatch) return Number(minuteMatch[1]) * 60 + Number(minuteMatch[2]);
  const secondMatch = text.match(/^(\d+)s$/);
  if (secondMatch) return Number(secondMatch[1]);
  return 0;
};
const mergeElapsedFromServer = serverElapsed => {
  if (!serverElapsed) return;
  const serverSeconds = elapsedToSeconds(serverElapsed);
  const localSeconds = elapsedToSeconds(elapsedTime.value);
  // Never regress local elapsed time with stale backend values (e.g. 0s).
  if (serverSeconds >= localSeconds) {
    elapsedTime.value = serverElapsed;
  }
};
const percentText = computed(() => {
  const total = Number(progress.value.total) || 0;
  const processed = Number(progress.value.processed) || 0;
  if (!total) return '0% - 0 / 0';
  const percent = (processed / total) * 100;
  if (Number.isInteger(percent)) return `${percent}% - ${processed} / ${total}`;
  return `${percent.toFixed(1).replace('.', ',')}% - ${processed} / ${total}`;
});
const percentProgressComputed = computed(() => {
  const total = Number(progress.value.total) || 0;
  const processed = Number(progress.value.processed) || 0;
  if (!total) return '0%';
  const percent = (processed / total) * 100;
  return `${percent}%`;
});
const migrationTargetTotal = computed(() => {
  const totalEligibleFromProgress = Number(progress.value.eligible_total) || 0;
  const totalEligibleFromResult = Number(result.value?.eligible_total) || 0;
  const totalFromResult = Number(result.value?.total) || 0;
  const totalFromProgress = Number(progress.value.total) || 0;
  return (
    totalEligibleFromProgress ||
    totalEligibleFromResult ||
    totalFromResult ||
    totalFromProgress
  );
});
const migratedCount = computed(() => {
  const compliantFromProgress = Number(progress.value.compliant_count);
  if (!Number.isNaN(compliantFromProgress) && compliantFromProgress >= 0) {
    return compliantFromProgress;
  }
  const compliantFromResult = Number(result.value?.compliant_count);
  if (!Number.isNaN(compliantFromResult) && compliantFromResult >= 0) {
    return compliantFromResult;
  }
  const migratedFromResult = Number(result.value?.migrated);
  if (!Number.isNaN(migratedFromResult) && migratedFromResult >= 0) {
    return migratedFromResult;
  }
  return Number(progress.value.processed) || 0;
});
const pendingCount = computed(() =>
  Math.max(
    Number(progress.value.non_compliant_count) ||
      Number(result.value?.non_compliant_count) ||
      migrationTargetTotal.value - migratedCount.value,
    0
  )
);
const newStylePercent = computed(() => {
  if (!migrationTargetTotal.value) return 100;
  return (migratedCount.value / migrationTargetTotal.value) * 100;
});
const oldStylePercent = computed(() => {
  if (!migrationTargetTotal.value) return 0;
  return (pendingCount.value / migrationTargetTotal.value) * 100;
});
const formatPercent = value => {
  if (Number.isInteger(value)) return `${value}%`;
  return `${Number(value || 0)
    .toFixed(1)
    .replace('.', ',')}%`;
};

// Title text for the start button, computed to avoid complex template expressions
const startButtonTitle = computed(() => {
  if (progress.value && progress.value.status === 'running')
    return t('INBOX_MGMT.STORAGE_FOLDER.IN_PROGRESS');
  if (progress.value && progress.value.status === 'paused')
    return t('INBOX_MGMT.STORAGE_FOLDER.PAUSED');
  if (
    props &&
    props.storageMigrationStatus &&
    ['running', 'paused'].includes(props.storageMigrationStatus)
  ) {
    return props.storageMigrationStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_MIGRATION_FINISH')
      : t('INBOX_MGMT.STORAGE_MIGRATION.PAUSED');
  }
  if (
    props &&
    props.existenceStatus &&
    ['running', 'paused'].includes(props.existenceStatus)
  ) {
    return props.existenceStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_DETAILED_FINISH')
      : t('INBOX_MGMT.STORAGE_ANALYSIS.DETAILED_ANALYSIS_PAUSED');
  }
  return '';
});

const elapsedTime = ref('0s');
const emitFolderStyleStatus = status => {
  try {
    window.dispatchEvent(
      new CustomEvent('folder_style_migration:status', {
        detail: { inboxId: props.inboxId, status: status || 'not_started' },
      })
    );
  } catch (e) {
    // non-blocking
  }
};

watch(
  () => progress.value.status,
  status => {
    emitFolderStyleStatus(status);
  },
  { immediate: true }
);

const updateElapsed = () => {
  if (!startTime) return;
  const seconds = Math.floor((Date.now() - startTime) / 1000);
  if (seconds < 60) elapsedTime.value = `${seconds}s`;
  else elapsedTime.value = `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

const stopRerunStatePolling = () => {
  if (rerunStateInterval) {
    clearInterval(rerunStateInterval);
    rerunStateInterval = null;
  }
  if (rerunStateTimeout) {
    clearTimeout(rerunStateTimeout);
    rerunStateTimeout = null;
  }
};

const startRerunStatePolling = () => {
  stopRerunStatePolling();
  rerunStateInterval = setInterval(() => {
    if (loadingDetailed.value) return;
    runDetailedFolderStyleAnalysis();
  }, 5000);
  rerunStateTimeout = setTimeout(() => {
    stopRerunStatePolling();
  }, 60000);
};

const startMigration = async () => {
  if (pendingCount.value <= 0) return;

  loading.value = true;
  error.value = null;
  hasRun.value = true;
  progress.value = { status: 'running', processed: 0, total: 0 };
  result.value = { migrated: 0, total: 0 };
  elapsedTime.value = '0s';
  try {
    const { data } = await axios.post(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/start`
    );
    currentSessionId.value = data?.session_id || null;
    if (data) progress.value = { ...progress.value, ...data };
    // Ensure last-execution panel is visible so progress bar shows immediately
    showLastExecution.value = true;
    pollProgress();
    startTime = Date.now();
    clearInterval(elapsedInterval);
    elapsedInterval = setInterval(updateElapsed, 1000);
    // persist initial progress state
    await saveProgressToBackend();
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao iniciar organização.';
    loading.value = false;
  }
};

const runDetailedFolderStyleAnalysis = async () => {
  loadingDetailed.value = true;
  error.value = null;
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/state`
    );
    currentSessionId.value = data?.session_id || currentSessionId.value;
    progress.value = data;
    hasRun.value = true;
    showLastExecution.value = true;

    if (data?.updated_at) {
      analysisDate.value = new Date(data.updated_at).toLocaleDateString(
        'pt-BR'
      );
    }

    if (data?.status === 'running') {
      stopRerunStatePolling();
      mergeElapsedFromServer(data.elapsed_time);
      let elapsedSeconds = 0;
      if (elapsedTime.value && elapsedTime.value.includes('m')) {
        const match = elapsedTime.value.match(/(\d+)m\s*(\d+)s/);
        if (match) {
          elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        }
      } else {
        elapsedSeconds =
          parseInt((elapsedTime.value || '0s').replace('s', ''), 10) || 0;
      }
      startTime = Date.now() - elapsedSeconds * 1000;
      clearInterval(elapsedInterval);
      elapsedInterval = setInterval(updateElapsed, 1000);
      pollProgress();
      return;
    }

    if (data?.status === 'finished') {
      mergeElapsedFromServer(data.elapsed_time);
      await fetchResult();
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      'Failed to run folder style detailed analysis.';
  } finally {
    loadingDetailed.value = false;
  }
};

const rerunDetailedFolderStyleAnalysis = async () => {
  await runDetailedFolderStyleAnalysis();
  startRerunStatePolling();
};

const pauseMigration = async () => {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/pause`
    );
    // Pause local elapsed timer immediately to keep UI consistent with paused state.
    progress.value = Object.assign({}, progress.value, { status: 'paused' });
    clearInterval(elapsedInterval);
    // Persist elapsed time/status to backend so state can be restored after reload
    await saveProgressToBackend();
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Failed to pause folder style migration.';
  }
};

const resumeMigration = async () => {
  loading.value = true;
  error.value = null;
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/resume`
    );
    // Immediately mark local state as running and persist it to backend to avoid races
    progress.value = Object.assign({}, progress.value, { status: 'running' });
    try {
      await saveProgressToBackend();
    } catch (e) {
      // non-blocking
      error.value =
        e?.message || 'Failed to persist running state after resume.';
    }

    // Fetch the latest state so UI can react immediately
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/state`
    );
    currentSessionId.value = data?.session_id || currentSessionId.value;
    progress.value = data;
    hasRun.value = true;
    // If resumed, restore timers and start polling
    if (data && data.status === 'running') {
      // restore elapsed_time if provided
      mergeElapsedFromServer(data.elapsed_time);
      let elapsedSeconds = 0;
      if (elapsedTime.value && elapsedTime.value.includes('m')) {
        const match = elapsedTime.value.match(/(\d+)m\s*(\d+)s/);
        if (match)
          elapsedSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
      } else {
        elapsedSeconds =
          parseInt((elapsedTime.value || '0s').replace('s', ''), 10) || 0;
      }
      startTime = Date.now() - elapsedSeconds * 1000;
      clearInterval(elapsedInterval);
      elapsedInterval = setInterval(updateElapsed, 1000);
      pollProgress();
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Failed to resume folder style migration.';
  } finally {
    loading.value = false;
  }
};

const stopMigration = async () => {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/stop`
    );
    // Fetch the latest progress so UI shows the 'stopped' state
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/progress`
    );
    progress.value = data;
    // Clear polling/timers and UI result to indicate interruption
    clearInterval(pollInterval);
    clearInterval(elapsedInterval);
    loading.value = false;
    // Clear detailed result since migration was interrupted
    result.value = null;
    hasRun.value = true;
    // Persist that migration was stopped and clear elapsed time
    await saveProgressToBackend();
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Failed to stop folder style migration.';
  }
};

const restartMigration = async () => {
  await axios.post(
    `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/restart`
  );
  // Reveal panel so user sees progress immediately after restart
  showLastExecution.value = true;
  pollProgress();
};

const toggleShowLastExecution = async () => {
  showLastExecution.value = !showLastExecution.value;
  if (showLastExecution.value) {
    try {
      // When showing last execution, fetch current state and update UI
      const { data } = await axios.get(
        `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/state`
      );
      currentSessionId.value = data?.session_id || currentSessionId.value;
      progress.value = data;
      if (data && data.updated_at)
        analysisDate.value = new Date(data.updated_at).toLocaleDateString(
          'pt-BR'
        );
      if (['running', 'paused'].includes(data.status)) {
        // Restore elapsed time if provided
        mergeElapsedFromServer(data.elapsed_time);
        // start polling to keep UI live
        pollProgress();
      } else if (data.status === 'finished') {
        // Restore elapsed time and load final cached result
        mergeElapsedFromServer(data.elapsed_time);
        await fetchResult();
      }
    } catch (e) {
      error.value =
        e?.response?.data?.message || 'Failed to load last execution state.';
    }
  }
};

async function pollProgress() {
  const update = async () => {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/progress`
    );
    const previousSessionId = currentSessionId.value;
    const incomingSessionId = data?.session_id || null;
    if (
      incomingSessionId &&
      previousSessionId &&
      incomingSessionId !== previousSessionId
    ) {
      result.value = { migrated: 0, total: 0 };
      elapsedTime.value = '0s';
    }
    currentSessionId.value = incomingSessionId || currentSessionId.value;
    progress.value = data;
    hasRun.value = true;
    if (data.updated_at)
      analysisDate.value = new Date(data.updated_at).toLocaleDateString(
        'pt-BR'
      );
    // Restore elapsed_time from backend if available so UI shows the cached duration
    mergeElapsedFromServer(data.elapsed_time);
    if (data.status === 'paused') {
      clearInterval(elapsedInterval);
    }
    // Persist backend-provided elapsed_time/status so UI state is durable
    await saveProgressToBackend();
    if (data.status === 'finished') {
      clearInterval(pollInterval);
      clearInterval(elapsedInterval);
      loading.value = false;
      await fetchResult();
    }
  };
  await update();
  clearInterval(pollInterval);
  pollInterval = setInterval(update, 1000);
}

// Persist current progress to backend (used to make UI state durable across reloads)
async function saveProgressToBackend() {
  try {
    const elapsedSeconds = elapsedToSeconds(elapsedTime.value);
    const payload = {
      processed: progress.value.processed,
      status: progress.value.status,
      session_id: currentSessionId.value,
    };
    // Avoid persisting stale 0s while migration already has processed files.
    if (
      elapsedSeconds > 0 ||
      !['running', 'paused'].includes(progress.value.status) ||
      Number(progress.value.processed || 0) === 0
    ) {
      payload.elapsedTime = elapsedTime.value;
    }
    // Only include total when it's known (> 0) to avoid overwriting server-side total with 0
    if (Number(progress.value.total) > 0) payload.total = progress.value.total;

    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/save_progress`,
      payload
    );
  } catch (e) {
    // non-blocking
    error.value = t('INBOX_MGMT.STORAGE_FOLDER.ERROR_SAVE_PROGRESS');
  }
}

onMounted(async () => {
  try {
    // Restore backend state if present, but keep last-execution hidden by default
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/state`
    );
    currentSessionId.value = data?.session_id || null;
    progress.value = data;
    hasRun.value = true;

    // Restore elapsed time / timers and auto-show last-execution when relevant
    if (data && data.status) {
      if (['running', 'paused'].includes(data.status)) {
        // Auto-open the last-execution panel so users see the current/paused run immediately
        showLastExecution.value = true;

        elapsedTime.value = data.elapsed_time || '0s';
        let elapsedSeconds = 0;
        if (elapsedTime.value.includes('m')) {
          const match = elapsedTime.value.match(/(\d+)m\s*(\d+)s/);
          if (match)
            elapsedSeconds =
              parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        } else {
          elapsedSeconds =
            parseInt((elapsedTime.value || '0s').replace('s', ''), 10) || 0;
        }
        startTime = Date.now() - elapsedSeconds * 1000;
        clearInterval(elapsedInterval);
        if (data.status === 'running') {
          // start polling/timers immediately when running
          elapsedInterval = setInterval(updateElapsed, 1000);
          pollProgress();
        }
      }
      if (data.status === 'finished') {
        // final result is cached in Redis; do not fetch until user toggles to view
        mergeElapsedFromServer(data.elapsed_time);
      }
    }
  } catch (e) {
    // ignore - best effort restoration
    error.value = e?.message || 'Failed to restore folder migration state.';
  }
});

onUnmounted(() => {
  clearInterval(pollInterval);
  clearInterval(elapsedInterval);
  stopRerunStatePolling();
});

async function fetchResult() {
  const { data } = await axios.get(
    `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}`
  );
  if (
    currentSessionId.value &&
    data?.session_id &&
    data.session_id !== currentSessionId.value
  )
    return;
  result.value = data;
}

const migratedList = computed(() => {
  if (!result.value || !Array.isArray(result.value.details)) return [];
  return result.value.details.filter(d => d.migrated === true);
});

const failedList = computed(() => {
  if (!result.value || !Array.isArray(result.value.details)) return [];
  return result.value.details.filter(d => !d.migrated);
});

const reorganizedCount = computed(() => {
  const fromResult = Number(result.value?.migrated);
  if (!Number.isNaN(fromResult) && fromResult >= 0) return fromResult;
  const fromProgress = Number(progress.value?.migrated_count);
  if (!Number.isNaN(fromProgress) && fromProgress >= 0) return fromProgress;
  return migratedList.value.length;
});

const failuresCount = computed(() => {
  return failedList.value.length;
});

const hasDetailedResult = computed(() => {
  return (
    Array.isArray(result.value?.details) && result.value.details.length > 0
  );
});

const downloadOrphans = async () => {
  try {
    // this endpoint responds with a CSV file
    const url = `/api/v1/inboxes/${props.inboxId}/folder_style_migrations/${props.inboxId}/download_orphans`;
    window.open(url, '_blank');
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      t('INBOX_MGMT.STORAGE_FOLDER.ERROR_DOWNLOAD');
  }
};
</script>

<template>
  <div>
    <div class="grid grid-cols-3 items-center mb-2">
      <div class="flex items-center gap-2 justify-start">
        <h3 class="text-lg font-semibold text-n-brand">
          {{ t('INBOX_MGMT.STORAGE_FOLDER.TITLE') }}
        </h3>
      </div>
      <div class="flex justify-center">
        <button
          class="inline-flex items-center gap-2 whitespace-nowrap px-3 py-1 rounded bg-slate-700 text-white text-sm font-medium hover:brightness-110"
          :aria-pressed="!showLastExecution"
          @click="toggleShowLastExecution"
        >
          {{
            showLastExecution
              ? t('INBOX_MGMT.STORAGE_ANALYSIS.HIDE_LAST_EXECUTION')
              : t('INBOX_MGMT.STORAGE_ANALYSIS.SHOW_LAST_EXECUTION')
          }}
        </button>
      </div>
      <div class="flex justify-end">
        <div class="flex items-center gap-2">
          <button
            v-if="hasRun"
            :disabled="
              loadingDetailed ||
              ['running', 'paused'].includes(progress.status) ||
              ['running', 'paused'].includes(storageMigrationStatus) ||
              ['running', 'paused'].includes(existenceStatus)
            "
            :title="startButtonTitle"
            class="inline-flex items-center gap-2 px-4 py-2 rounded bg-yellow-500 !text-slate-900 dark:!text-slate-900 text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0"
            @click="rerunDetailedFolderStyleAnalysis"
          >
            <span class="!text-slate-900 dark:!text-slate-900">{{
              t('INBOX_MGMT.STORAGE_ANALYSIS.RERUN_DETAILED_ANALYSIS')
            }}</span>
          </button>

          <button
            v-else
            :disabled="
              loadingDetailed ||
              ['running', 'paused'].includes(progress.status) ||
              ['running', 'paused'].includes(storageMigrationStatus) ||
              ['running', 'paused'].includes(existenceStatus)
            "
            :title="startButtonTitle"
            class="inline-flex items-center gap-2 px-4 py-2 rounded bg-slate-700 text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0"
            @click="rerunDetailedFolderStyleAnalysis"
          >
            {{ t('INBOX_MGMT.STORAGE_ANALYSIS.START_DETAILED_ANALYSIS') }}
          </button>

          <button
            :disabled="pendingCount <= 0"
            :title="startButtonTitle"
            class="inline-flex items-center gap-2 px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0"
            @click="startMigration"
          >
            <svg
              v-if="loading"
              class="animate-spin h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            {{ t('INBOX_MGMT.STORAGE_FOLDER.RUN') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="hasRun" class="ml-0 mr-0 py-0 w-full">
      <div v-if="error" class="text-red-600">{{ error }}</div>
      <div v-else>
        <div
          v-if="showLastExecution && progress"
          class="mt-3 mb-3 flex flex-col md:flex-row md:gap-3 justify-center items-center"
        >
          <div
            class="bg-white dark:bg-[#23262b] border border-slate-200 dark:border-[#35373c] rounded-lg p-6 min-w-[320px] max-w-md flex-1 flex flex-col justify-between m-0"
          >
            <div class="grid grid-cols-2 mb-2">
              <div class="text-left">
                <span
                  class="text-xs text-slate-600 dark:text-slate-400 font-medium"
                >
                  Compliance
                </span>
                <div class="text-2xl font-bold text-amber-600">
                  {{ formatPercent(oldStylePercent) }}
                </div>
              </div>
              <div class="text-right">
                <span
                  class="text-lg text-slate-900 dark:text-white font-medium"
                >
                  Old style
                </span>
                <div class="text-4xl font-bold text-n-brand">
                  {{ pendingCount }}
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between mb-1 mt-4">
              <span />
              <span class="text-xs text-slate-400 font-semibold text-right">
                Old count
              </span>
            </div>
          </div>
          <div
            class="bg-white dark:bg-[#23262b] border border-slate-200 dark:border-[#35373c] rounded-lg p-6 min-w-[320px] max-w-md flex-1 flex flex-col justify-between m-0"
          >
            <div class="grid grid-cols-2 mb-2">
              <div class="text-center justify-self-start">
                <span
                  class="text-lg text-slate-900 dark:text-white font-medium"
                >
                  New style
                </span>
                <div class="text-4xl font-bold text-n-brand">
                  {{ migratedCount }}
                </div>
              </div>
              <div class="text-right">
                <span
                  class="text-xs text-slate-600 dark:text-slate-400 font-medium"
                >
                  Compliance
                </span>
                <div class="text-2xl font-bold text-green-600">
                  {{ formatPercent(newStylePercent) }}
                </div>
              </div>
            </div>
            <div class="text-xs text-slate-400 font-semibold mb-1 mt-4">
              New count
            </div>
          </div>
        </div>
        <div class="w-full mt-4">
          <div
            v-if="
              showLastExecution &&
              progress &&
              ['running', 'paused', 'finished'].includes(progress.status)
            "
          >
            <div
              class="w-full bg-slate-700 rounded h-5 mb-1 relative overflow-hidden"
            >
              <div
                class="h-5 rounded transition-all duration-700"
                :class="
                  progress.status === 'finished' ? 'bg-green-500' : 'bg-n-brand'
                "
                :style="{ width: percentProgressComputed }"
              />
              <span
                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold px-2 text-white"
              >
                {{ percentText }}
              </span>
            </div>

            <div class="grid grid-cols-3 items-center mb-1">
              <div />
              <div
                class="text-center text-xs text-slate-700 dark:text-slate-400"
              >
                <span v-if="analysisDate">
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.LAST_ANALYSIS') }}:
                  {{ analysisDate }}
                </span>
              </div>
              <div class="text-right text-xs">
                <span
                  v-if="progress.status === 'finished'"
                  class="text-green-400 font-semibold"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.COMPLETED') }}
                </span>
              </div>
            </div>

            <div
              v-if="['running', 'paused', 'finished'].includes(progress.status)"
              class="text-center text-sm text-slate-700 dark:text-slate-400 mt-2"
            >
              {{ t('INBOX_MGMT.STORAGE_ANALYSIS.ELAPSED_TIME') }}:
              {{ elapsedTime }}
            </div>

            <div class="flex justify-between mt-2">
              <button
                v-if="progress.status === 'running'"
                class="px-4 py-2 bg-yellow-500 text-black !text-black dark:!text-black rounded hover:brightness-110"
                @click="pauseMigration"
              >
                <span class="text-black !text-black dark:!text-black">
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.PAUSE') }}
                </span>
              </button>
              <div v-if="progress.status === 'paused'">
                <button
                  class="px-4 py-2 bg-green-500 text-white rounded hover:brightness-110"
                  @click="resumeMigration"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESUME') }}
                </button>
                <button
                  class="inline-flex items-center gap-2 px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0 ml-2"
                  @click="restartMigration"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESTART') }}
                </button>
              </div>
              <button
                v-if="['running', 'paused'].includes(progress.status)"
                class="px-4 py-2 bg-red-500 text-white rounded hover:brightness-110"
                @click="stopMigration"
              >
                {{ t('INBOX_MGMT.STORAGE_ANALYSIS.STOP') }}
              </button>
            </div>
          </div>

          <div
            v-if="
              showLastExecution &&
              progress &&
              progress.status === 'finished' &&
              pendingCount > 0
            "
            class="mt-4"
          >
            <h5 class="text-base font-semibold text-n-brand mb-2 text-center">
              {{ t('INBOX_MGMT.STORAGE_FOLDER.SUMMARY') }}
            </h5>

            <div
              class="rounded-lg p-4 bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
            >
              <div
                class="text-center font-semibold text-slate-900 dark:text-white"
              >
                {{
                  t('INBOX_MGMT.STORAGE_FOLDER.MIGRATED_COUNT', {
                    migrated: result.migrated,
                    total: result.total,
                  })
                }}
              </div>

              <!-- If nothing was migrated and there are failures, show the CSV warning -->
              <div
                v-if="
                  result.total && result.migrated === 0 && failedList.length > 0
                "
                class="mt-2 text-sm text-yellow-600 dark:text-yellow-300 text-center font-medium"
              >
                <span class="text-slate-900 dark:text-white font-medium">{{
                  t('INBOX_MGMT.STORAGE_FOLDER.WARNING_TITLE')
                }}</span>
                <span class="ml-1 text-slate-700 dark:text-slate-200">{{
                  t('INBOX_MGMT.STORAGE_FOLDER.WARNING_ORPHANS')
                }}</span>
              </div>

              <!-- Otherwise, if some files were migrated, show the success message -->
              <div
                v-else-if="result.migrated > 0"
                class="mt-2 text-sm text-yellow-600 dark:text-yellow-300 text-center font-medium"
              >
                <span class="text-slate-900 dark:text-white font-medium">{{
                  t('INBOX_MGMT.STORAGE_FOLDER.WARNING_TITLE')
                }}</span>
                <span class="ml-1 text-slate-700 dark:text-slate-200">{{
                  t('INBOX_MGMT.STORAGE_FOLDER.SUCCESS_MSG')
                }}</span>
              </div>
            </div>

            <div v-if="hasDetailedResult" class="mt-4">
              <h5 class="text-base font-semibold text-n-brand mb-2 text-center">
                {{ t('INBOX_MGMT.STORAGE_FOLDER.DETAILS') }}
              </h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Old style (encontrados / não encontrados) -->
                <div>
                  <div
                    class="rounded-lg p-4 flex flex-col gap-3 bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
                  >
                    <div
                      class="font-semibold text-n-brand mb-2 text-lg text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_FOLDER.OLD_FOLDER') }}
                    </div>
                    <div
                      class="rounded-lg p-3 bg-green-50/80 border border-green-200"
                    >
                      <div
                        class="text-xs text-green-800 font-semibold mb-1 text-center"
                      >
                        {{ t('INBOX_MGMT.STORAGE_FOLDER.FOUND') }}
                      </div>
                      <div
                        class="text-sm text-slate-900 dark:text-white text-center font-semibold"
                      >
                        {{ pendingCount }}
                        {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }}
                      </div>
                    </div>
                    <div
                      class="rounded-lg p-3 bg-red-50/80 border border-red-200"
                    >
                      <div
                        class="text-xs text-red-800 font-semibold mb-1 text-center"
                      >
                        {{ t('INBOX_MGMT.STORAGE_FOLDER.NOT_FOUND') }}
                      </div>
                      <div
                        class="text-sm text-slate-900 dark:text-white text-center font-semibold"
                      >
                        {{ failuresCount }}
                        {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- New folder style (migrated / errors) -->
                <div>
                  <div
                    class="rounded-lg p-4 flex flex-col gap-3 bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
                  >
                    <div
                      class="font-semibold text-n-brand mb-2 text-lg text-center"
                    >
                      {{ t('INBOX_MGMT.STORAGE_FOLDER.NEW_FOLDER') }}
                    </div>
                    <div
                      class="rounded-lg p-3 bg-green-50/80 border border-green-200"
                    >
                      <div
                        class="text-xs text-green-800 font-semibold mb-1 text-center"
                      >
                        {{ t('INBOX_MGMT.STORAGE_FOLDER.REORGANIZED') }}
                      </div>
                      <div
                        class="text-sm text-slate-900 dark:text-white text-center font-semibold"
                      >
                        {{ reorganizedCount }} arquivos
                      </div>
                    </div>
                    <div
                      class="rounded-lg p-3 bg-red-50/80 border border-red-200"
                    >
                      <div
                        class="text-xs text-red-800 font-semibold mb-1 text-center"
                      >
                        {{ t('INBOX_MGMT.STORAGE_FOLDER.FAILS') }}
                      </div>
                      <div
                        class="text-sm text-slate-900 dark:text-white text-center font-semibold"
                      >
                        {{ failuresCount }}
                        {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4">
                <div
                  v-if="failedList.length > 0"
                  class="flex items-center justify-end gap-2 mb-2"
                >
                  <button
                    class="px-3 py-1 bg-slate-700 text-white rounded text-sm hover:brightness-110"
                    @click="downloadOrphans"
                  >
                    {{ t('INBOX_MGMT.STORAGE_FOLDER.DOWNLOAD_ORPHANS') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
